import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Collapse, Column, Row, Typography } from 'seagull';
import { ChevronDown, ChevronUp } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import styles from './ConfirmationHierarchyMessage.module.scss';
import { FeedbackPromptBar } from './FeedbackPromptBar';
import { MessageFrame } from './MessageFrame';
import { ProposalActionsRow } from './ProposalActionsRow';
import { ProposalItemCard } from './ProposalItemCard';
import { ProposalItemCheckbox } from './ProposalItemCheckbox';
export const GetConfirmationHierarchyMessageTID = (parentTestId)=>getTestIds(parentTestId);
export const ConfirmationHierarchyMessage = (param)=>{
    let { content, messageId, interactive, onDismiss, onApply, onSubmitFeedback, onDismissFeedback } = param;
    const { ConfirmationHierarchyMessageTID } = useMemo(()=>GetConfirmationHierarchyMessageTID(messageId), [
        messageId
    ]);
    const [openSectionIds, setOpenSectionIds] = useState(()=>interactive ? new Set(content.sections.filter((s)=>s.open).map((s)=>s.id)) : new Set());
    const [itemChecked, setItemChecked] = useState(()=>{
        const initial = {};
        content.sections.forEach((s)=>s.items.forEach((i)=>initial[i.id] = i.checked));
        return initial;
    });
    const toggleSection = (sectionId)=>setOpenSectionIds((prev)=>{
            const next = new Set(prev);
            if (next.has(sectionId)) next.delete(sectionId);
            else next.add(sectionId);
            return next;
        });
    const toggleItem = (itemId)=>setItemChecked((prev)=>_object_spread_props(_object_spread({}, prev), {
                [itemId]: !prev[itemId]
            }));
    const sectionSelectedCount = (items)=>items.filter((i)=>itemChecked[i.id]).length;
    const totalSelectedCount = content.sections.reduce((acc, s)=>acc + sectionSelectedCount(s.items), 0);
    const allItemIds = content.sections.flatMap((s)=>s.items.map((i)=>i.id));
    const selectedItemIds = allItemIds.filter((id)=>itemChecked[id]);
    return /*#__PURE__*/ _jsxs(Column, {
        gap: 12,
        width: "100%",
        children: [
            /*#__PURE__*/ _jsxs(MessageFrame, {
                testId: ConfirmationHierarchyMessageTID.Root(),
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        testId: ConfirmationHierarchyMessageTID.Intro(),
                        children: content.intro
                    }),
                    /*#__PURE__*/ _jsx(Column, {
                        gap: 8,
                        testId: ConfirmationHierarchyMessageTID.Sections(),
                        children: content.sections.map((section)=>{
                            const isOpen = openSectionIds.has(section.id);
                            return /*#__PURE__*/ _jsxs(Column, {
                                padding: 12,
                                className: styles.section,
                                testId: ConfirmationHierarchyMessageTID.Section(section.id),
                                children: [
                                    /*#__PURE__*/ _jsxs("button", {
                                        type: "button",
                                        className: styles.sectionHeader,
                                        onClick: ()=>toggleSection(section.id),
                                        "data-testid": ConfirmationHierarchyMessageTID.SectionToggle(section.id),
                                        children: [
                                            /*#__PURE__*/ _jsx(Typography, {
                                                variant: "paragraph2",
                                                testId: ConfirmationHierarchyMessageTID.SectionTitle(section.id),
                                                children: "".concat(section.title, " (").concat(sectionSelectedCount(section.items), ")")
                                            }),
                                            /*#__PURE__*/ _jsx(Typography, {
                                                skin: "secondary",
                                                children: isOpen ? /*#__PURE__*/ _jsx(ChevronUp, {
                                                    size: 20
                                                }) : /*#__PURE__*/ _jsx(ChevronDown, {
                                                    size: 20
                                                })
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(Collapse, {
                                        in: isOpen,
                                        unmountOnExit: true,
                                        children: section.renderKind === 'cards' ? /*#__PURE__*/ _jsx(Row, {
                                            gap: 16,
                                            padding: 12,
                                            className: styles.sectionContent,
                                            children: section.items.map((item)=>{
                                                const itemTestId = "".concat(ConfirmationHierarchyMessageTID.SectionProposalItemPrefix(section.id), "-").concat(item.id);
                                                return 'groups' in item ? /*#__PURE__*/ _jsx(ProposalItemCard, {
                                                    item: item,
                                                    checked: !!itemChecked[item.id],
                                                    disabled: !interactive,
                                                    onChange: ()=>toggleItem(item.id),
                                                    testId: itemTestId
                                                }, item.id) : /*#__PURE__*/ _jsx(ProposalItemCheckbox, {
                                                    item: item,
                                                    checked: !!itemChecked[item.id],
                                                    disabled: !interactive,
                                                    onChange: ()=>toggleItem(item.id),
                                                    testId: itemTestId,
                                                    className: styles.item
                                                }, item.id);
                                            })
                                        }) : /*#__PURE__*/ _jsx(Column, {
                                            gap: 8,
                                            padding: 12,
                                            className: styles.sectionContent,
                                            children: section.items.map((item)=>{
                                                if ('groups' in item) return null;
                                                return /*#__PURE__*/ _jsx(ProposalItemCheckbox, {
                                                    item: item,
                                                    checked: !!itemChecked[item.id],
                                                    disabled: !interactive,
                                                    onChange: ()=>toggleItem(item.id),
                                                    testId: "".concat(ConfirmationHierarchyMessageTID.SectionProposalItemPrefix(section.id), "-").concat(item.id),
                                                    className: styles.item
                                                }, item.id);
                                            })
                                        })
                                    })
                                ]
                            }, section.id);
                        })
                    }),
                    interactive && /*#__PURE__*/ _jsx(ProposalActionsRow, {
                        selectedCount: totalSelectedCount,
                        applySelectedLabel: content.applySelectedLabel,
                        applyAllLabel: content.applyAllLabel,
                        dismissLabel: content.dismissLabel,
                        applySelectedDisabled: selectedItemIds.length === 0,
                        onApplySelected: ()=>onApply(messageId, selectedItemIds),
                        onApplyAll: ()=>onApply(messageId, allItemIds),
                        onDismiss: ()=>onDismiss(messageId),
                        dismissTestId: ConfirmationHierarchyMessageTID.Dismiss(),
                        applySelectedTestId: ConfirmationHierarchyMessageTID.ApplySelected(),
                        applyAllTestId: ConfirmationHierarchyMessageTID.ApplyAll(),
                        className: styles.actions
                    })
                ]
            }),
            content.feedbackPrompt != null && onSubmitFeedback != null && /*#__PURE__*/ _jsx(FeedbackPromptBar, {
                prompt: content.feedbackPrompt,
                onSubmit: onSubmitFeedback,
                onDismiss: onDismissFeedback,
                testId: messageId
            })
        ]
    });
};
